import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import type { CommunicationListOptions } from "@shared-core/data/adapter";
import type { CommunicationItem } from "@shared-core/mock-data/communications";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { formatCount } from "@shared-core/domain/formatters";
import { CommunicationTimeline } from "../app/components/domain/customers/CommunicationTimeline";
import { ErrorPanel } from "../app/components/patterns/ErrorPanel";

const PAGE_SIZE = 25;

type TypeFilter = "all" | "email" | "meeting" | "note" | "ticket";

export function CommunicationsPage() {
  const dataAdapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") ?? undefined;

  const [customerName, setCustomerName] = useState<string | null>(null);
  useEffect(() => {
    if (!customerFilter) {
      setCustomerName(null);
      return;
    }
    dataAdapter
      .getCustomerById(customerFilter)
      .then((c) => {
        if (c) setCustomerName(c.customer.name);
      })
      .catch(() => {});
  }, [dataAdapter, customerFilter]);

  const [sort, setSort] = useState<string>("-date");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const { state, retry } = useListQuery(() => {
    const options: CommunicationListOptions = {
      sort,
      page,
      pageSize: PAGE_SIZE,
    };
    if (customerFilter) options.customerId = customerFilter;
    if (typeFilter !== "all") options.types = [typeFilter];
    if (q.trim()) options.q = q.trim();
    return dataAdapter.listCommunications(options);
  }, [dataAdapter, sort, typeFilter, q, page, customerFilter]);

  useEffect(() => {
    setPage(1);
  }, [sort, typeFilter, q, customerFilter]);

  const items: CommunicationItem[] =
    state.status === "success" ? state.data.items : [];
  const total = state.status === "success" ? state.data.total : 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const typeOptions: { id: TypeFilter; label: string }[] = [
    { id: "all", label: "All" },
    { id: "email", label: "Emails" },
    { id: "meeting", label: "Meetings" },
    { id: "note", label: "Notes" },
    { id: "ticket", label: "Tickets" },
  ];

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl mb-1">
          {customerFilter
            ? `Communications for ${customerName ?? "…"}`
            : "Communications"}
        </h1>
        {customerFilter ? (
          <CustomerScopeChip
            customerName={customerName}
            onDismiss={handleDismissCustomerScope}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            All communications across your portfolio
          </p>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <input
            type="text"
            placeholder="Search communications..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="px-3 py-2 border border-border rounded bg-background text-sm w-full sm:w-64"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-xs px-2 py-1.5 rounded border border-border bg-background"
          >
            <option value="-date">Newest first</option>
            <option value="date">Oldest first</option>
          </select>
        </div>

        {/* Type filter pills */}
        <div className="flex gap-2 flex-wrap">
          {typeOptions.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setTypeFilter(opt.id)}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${
                typeFilter === opt.id
                  ? "bg-primary text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Status / results count */}
      <div className="text-sm text-muted-foreground">
        {state.status === "loading"
          ? "Loading..."
          : state.status === "success"
            ? `${formatCount(total)} item${total !== 1 ? "s" : ""}`
            : null}
      </div>

      {/* Error */}
      {state.status === "error" && (
        <ErrorPanel message={state.message} onRetry={retry} />
      )}

      {/* Empty */}
      {state.status === "success" && items.length === 0 && (
        <div className="text-sm p-8 text-center border border-border rounded-lg space-y-1">
          <p className="font-medium text-foreground">
            No communications captured yet
          </p>
          <p className="text-muted-foreground">
            Connect your Outlook in Settings to pull in emails and meetings
            automatically.
          </p>
        </div>
      )}

      {/* Results */}
      {state.status === "success" && items.length > 0 && (
        <CommunicationTimeline items={items} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
